import { datadogRum } from '@datadog/browser-rum';
import { StatsigClientEventEmitterInterface } from '@statsig/client-core';

import { setExperimentAssignmentsContext } from '../datadog/logger';

type Assignments = Record<string, string | number | boolean>;

const assignments: Assignments = {};

/** Clears the in-memory accumulator. Test-only — not for production use. */
export function resetStatsigDatadogExposureAssignmentsForTests(): void {
  for (const key of Object.keys(assignments)) {
    delete assignments[key];
  }
}

function isPrimitive(value: unknown): value is string | number | boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function forwardPrimitiveParams(params: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(params)) {
    if (isPrimitive(value)) {
      datadogRum.addFeatureFlagEvaluation(key, value);
      assignments[key] = value;
    }
  }
}

/**
 * Subscribe to Statsig layer/experiment evaluation events and forward
 * primitive parameter assignments to Datadog RUM feature-flag context and
 * the telehealth.spa browser-logs global context.
 *
 * Register immediately after constructing the StatsigClient, before
 * initializeAsync, so no early evaluations are missed.
 */
export function subscribeStatsigExposuresToDatadog(client: StatsigClientEventEmitterInterface): void {
  client.on('layer_evaluation', ({ layer }) => {
    forwardPrimitiveParams(layer.__value);
    const arm = layer.groupName ?? layer.ruleID;
    if (arm) {
      datadogRum.addFeatureFlagEvaluation(layer.name, arm);
      assignments[layer.name] = arm;
    }
    setExperimentAssignmentsContext({ ...assignments });
  });

  client.on('experiment_evaluation', ({ experiment }) => {
    forwardPrimitiveParams(experiment.value);
    const arm = experiment.groupName ?? experiment.ruleID;
    if (arm) {
      datadogRum.addFeatureFlagEvaluation(experiment.name, arm);
      assignments[experiment.name] = arm;
    }
    setExperimentAssignmentsContext({ ...assignments });
  });
}
